import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Link, Skeleton, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  categoryLabel,
  client,
  withOpenIncidents,
  type Application,
  type Department,
  type Incident,
  type ServiceStatus,
} from '../lib/api';
import { PageHeader, Section } from '../components/Page';
import { tokens } from '../theme';

type Key = ServiceStatus['status'];

/** Colours per service status. Text colours pass 4.5:1 on their soft backgrounds. */
const TONE: Record<Key, { label: string; fg: string; bg: string; soft: string; line: string; dot: string }> = {
  operational: { label: 'Operational', fg: '#067647', bg: '#ECFDF3', soft: '#FFFFFF', line: '#ABEFC6', dot: '#17B26A' },
  degraded: { label: 'Degraded', fg: '#93370D', bg: '#FFFAEB', soft: '#FFFCF5', line: '#FEDF89', dot: '#F79009' },
  outage: { label: 'Outage', fg: '#B42318', bg: '#FEF3F2', soft: '#FFFBFA', line: '#FECDCA', dot: '#F04438' },
  maintenance: { label: 'Maintenance', fg: '#004C8C', bg: '#EAF2FB', soft: '#F5F9FE', line: '#B2D1F0', dot: '#005EAD' },
};
const KEYS: Key[] = ['operational', 'degraded', 'outage', 'maintenance'];

const BANNER: Record<Key, { title: string; verb: string; Icon: typeof CheckIcon }> = {
  operational: { title: 'All systems operational', verb: '', Icon: CheckIcon },
  degraded: { title: 'Some systems are degraded', verb: 'running slower than usual', Icon: WarningAmberIcon },
  outage: { title: 'Partial outage', verb: 'down', Icon: CloseIcon },
  maintenance: { title: 'Planned maintenance', verb: 'under maintenance', Icon: BuildOutlinedIcon },
};

const REFRESH_MS = 60_000;

function tone(status: string) {
  return TONE[(status as Key) in TONE ? (status as Key) : 'operational'];
}

function when(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function listNames(names: string[]) {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function StatusPill({ status }: { status: string }) {
  const t = tone(status);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: t.bg,
        color: t.fg,
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        flex: 'none',
      }}
    >
      <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.dot }} />
      {t.label}
    </Box>
  );
}

export function StatusPage() {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [apps, setApps] = useState<Application[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    const [status, catalog] = await Promise.all([
      client.status(),
      // Grouping is a nice-to-have; the page still works without the catalog.
      client.catalog().catch(() => null),
    ]);
    setServices(withOpenIncidents(status.services, status.incidents));
    setIncidents(status.incidents);
    if (catalog) {
      setApps(catalog.applications);
      setDepartments(catalog.departments);
    }
    setCheckedAt(new Date());
    setError('');
  }, []);

  useEffect(() => {
    load()
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load().catch(() => undefined);
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  async function refresh() {
    setRefreshing(true);
    try {
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setRefreshing(false);
    }
  }

  const overall: Key = (['outage', 'degraded', 'maintenance'] as Key[]).find((k) => services.some((s) => s.status === k)) ?? 'operational';
  const banner = BANNER[overall];
  const bt = TONE[overall];
  const affected = services.filter((s) => s.status !== 'operational');
  const healthy = services.length - affected.length;

  const bannerSub =
    overall === 'operational'
      ? 'Every IT Lab application is up and running normally.'
      : affected
          .reduce<Array<{ key: Key; names: string[] }>>((acc, s) => {
            const g = acc.find((x) => x.key === s.status);
            if (g) g.names.push(s.name);
            else acc.push({ key: s.status, names: [s.name] });
            return acc;
          }, [])
          .map((g) => `${listNames(g.names)} ${g.names.length > 1 ? 'are' : 'is'} ${BANNER[g.key].verb}.`)
          .join(' ') + (healthy > 0 ? ' Everything else is working normally.' : '');

  const groups = useMemo(() => {
    const appById = new Map(apps.map((a) => [a.id, a]));
    const rows = services.map((s) => {
      const app = s.application_id ? appById.get(s.application_id) : undefined;
      const kind = app ? `${categoryLabel(app.category)}${app.status === 'beta' ? ' · Beta' : ''}` : s.application_id ? '' : 'This site';
      return { service: s, app, kind, group: app?.department_id ?? (s.application_id ? '__other' : '__platform') };
    });
    const out: Array<{ id: string; name: string; rows: typeof rows }> = [];
    const push = (id: string, name: string) => {
      const list = rows.filter((r) => r.group === id);
      if (list.length) out.push({ id, name, rows: list });
    };
    push('__platform', 'Platform');
    [...departments].sort((a, b) => a.sort_order - b.sort_order).forEach((d) => push(d.id, d.name));
    const known = new Set(['__platform', ...departments.map((d) => d.id)]);
    const rest = rows.filter((r) => !known.has(r.group));
    if (rest.length) out.push({ id: '__other', name: departments.length ? 'Other' : 'Applications', rows: rest });
    return out;
  }, [services, apps, departments]);

  const active = incidents.filter((i) => !i.resolved_at);
  const resolved = incidents.filter((i) => i.resolved_at);
  const serviceFor = (i: Incident) => services.find((s) => s.id === i.service_id);

  return (
    <>
      <PageHeader title="System status" subtitle="Live health of every IT Lab application. If something isn’t working, check here before raising a ticket.">
        {loading ? (
          <Skeleton variant="rounded" height={104} />
        ) : error && !services.length ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            gap={{ xs: 2, md: 2.5 }}
            flexWrap="wrap"
            aria-live="polite"
            sx={{ px: { xs: 2.5, md: 3.5 }, py: { xs: 2.5, md: 3 }, borderRadius: 2, bgcolor: bt.bg, border: `1px solid ${bt.line}` }}
          >
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: bt.dot, color: '#fff', display: 'grid', placeItems: 'center', flex: 'none' }}>
              <banner.Icon sx={{ fontSize: 30 }} aria-hidden />
            </Box>
            <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
              <Typography component="h2" sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, letterSpacing: '-0.02em', color: tokens.ink, lineHeight: 1.2 }}>
                {banner.title}
              </Typography>
              <Typography fontSize={16} color="#344054" mt={0.5}>
                {bannerSub}
              </Typography>
            </Box>
            <Stack spacing={0.75} alignItems="flex-start">
              {checkedAt && (
                <Typography fontSize={14} color={tokens.text2}>
                  Checked {checkedAt.toLocaleTimeString(undefined, { timeStyle: 'short' })}
                </Typography>
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refresh}
                disabled={refreshing}
                sx={{ bgcolor: '#fff', minHeight: 44, '&:hover': { bgcolor: '#fff' } }}
              >
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
            </Stack>
          </Stack>
        )}
      </PageHeader>

      <Section py={{ xs: 6, md: 7 }}>
        <Stack spacing={{ xs: 6, md: 7 }}>
          {error && services.length > 0 && <Alert severity="warning">Couldn’t refresh just now: {error}</Alert>}

          {active.length > 0 && (
            <Stack component="section" aria-labelledby="active-h" spacing={2}>
              <Typography id="active-h" variant="h2" fontSize={24}>
                {active.length === 1 ? 'Active incident' : 'Active incidents'}
              </Typography>
              {active.map((i) => {
                const svc = serviceFor(i);
                const t = tone(svc?.status ?? 'degraded');
                return (
                  <Box component="article" key={i.id} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2, border: `1px solid ${t.line}`, bgcolor: t.soft === '#FFFFFF' ? TONE.degraded.soft : t.soft }}>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" mb={1.25}>
                      <StatusPill status={svc?.status ?? 'degraded'} />
                      <Typography fontSize={14} color={tokens.text2}>
                        {svc?.name ?? 'Service'} · started {when(i.started_at)}
                      </Typography>
                    </Stack>
                    <Typography component="h3" fontWeight={700} fontSize={19} color={tokens.ink}>
                      {i.title}
                    </Typography>
                    {i.body && (
                      <Typography mt={0.75} color="#344054" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                        {i.body}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}

          <Stack component="section" aria-labelledby="svc-h" spacing={2.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" gap={2} flexWrap="wrap">
              <Box>
                <Typography id="svc-h" variant="h2" fontSize={24}>
                  Services
                </Typography>
                {!loading && services.length > 0 && (
                  <Typography fontSize={15} color={tokens.text2} mt={0.5}>
                    {healthy} of {services.length} services operational
                  </Typography>
                )}
              </Box>
              <Box component="ul" aria-label="Status key" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {KEYS.map((k) => (
                  <Box component="li" key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 13, color: '#344054' }}>
                    <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TONE[k].dot }} />
                    {TONE[k].label}
                  </Box>
                ))}
              </Box>
            </Stack>

            {loading &&
              [1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={150} />)}

            {groups.map((g) => {
              const ok = g.rows.filter((r) => r.service.status === 'operational').length;
              return (
                <Box key={g.id} sx={{ border: `1px solid ${tokens.border}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1.5}
                    sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, bgcolor: tokens.subtle, borderBottom: `1px solid ${tokens.divider}` }}
                  >
                    <Typography component="h3" fontWeight={700} fontSize={15} color={tokens.ink}>
                      {g.name}
                    </Typography>
                    <Typography fontSize={13} color={tokens.text2}>
                      {ok === g.rows.length ? 'All operational' : `${ok} of ${g.rows.length} operational`}
                    </Typography>
                  </Stack>
                  {g.rows.map(({ service: s, app, kind }, idx) => {
                    const t = tone(s.status);
                    return (
                      <Stack
                        key={s.id}
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        columnGap={3}
                        rowGap={1.25}
                        sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderTop: idx ? `1px solid ${tokens.divider}` : 0, bgcolor: t.soft }}
                      >
                        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                          {app ? (
                            <Link component={RouterLink} to={`/apps/${app.slug}`} color={tokens.ink} underline="hover" fontWeight={600} fontSize={16}>
                              {s.name}
                            </Link>
                          ) : (
                            <Typography fontWeight={600} fontSize={16} color={tokens.ink}>
                              {s.name}
                            </Typography>
                          )}
                          {kind && (
                            <Typography fontSize={13} color={tokens.text2}>
                              {kind}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: '2 1 260px', minWidth: 0 }}>
                          <Typography fontSize={14} color="#344054">
                            {s.message || 'All systems normal.'}
                          </Typography>
                          {s.status !== 'operational' && s.updated_at && (
                            <Typography fontSize={13} color={tokens.text2}>
                              Since {when(s.updated_at)}
                            </Typography>
                          )}
                        </Box>
                        <StatusPill status={s.status} />
                      </Stack>
                    );
                  })}
                </Box>
              );
            })}
          </Stack>

          <Stack component="section" aria-labelledby="hist-h" spacing={2}>
            <Box>
              <Typography id="hist-h" variant="h2" fontSize={24}>
                Past 14 days
              </Typography>
              <Typography fontSize={15} color={tokens.text2} mt={0.5}>
                Resolved incidents stay listed here for two weeks.
              </Typography>
            </Box>
            {!loading && resolved.length === 0 && (
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
                sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 2, border: `1px dashed ${tokens.border}`, bgcolor: '#FCFCFD' }}
              >
                <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: tokens.successSoft, display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <CheckIcon sx={{ color: tokens.success }} aria-hidden />
                </Box>
                <Box>
                  <Typography fontWeight={600} color={tokens.ink}>
                    No resolved incidents in the last 14 days
                  </Typography>
                  <Typography fontSize={14} color={tokens.text2}>
                    When IT Lab closes an incident, its summary appears here.
                  </Typography>
                </Box>
              </Stack>
            )}
            {resolved.length > 0 && (
              <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: 2, overflow: 'hidden' }}>
                {resolved.map((i, idx) => (
                  <Box key={i.id} component="article" sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderTop: idx ? `1px solid ${tokens.divider}` : 0 }}>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                      <Typography component="h3" fontWeight={600} fontSize={16} color={tokens.ink}>
                        {i.title}
                      </Typography>
                      <Box component="span" sx={{ px: 1.25, py: 0.25, borderRadius: 999, bgcolor: tokens.successSoft, color: tokens.success, fontSize: 13, fontWeight: 600 }}>
                        Resolved
                      </Box>
                    </Stack>
                    <Typography fontSize={14} color={tokens.text2} mt={0.25}>
                      {serviceFor(i)?.name ?? 'Service'} · {when(i.started_at)} – {when(i.resolved_at)}
                    </Typography>
                    {i.body && (
                      <Typography fontSize={14} mt={0.75} color="#344054" sx={{ whiteSpace: 'pre-line' }}>
                        {i.body}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Stack>

          <Stack
            component="section"
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ md: 'center' }}
            justifyContent="space-between"
            gap={3}
            sx={{ borderTop: `1px solid ${tokens.divider}`, pt: 5 }}
          >
            <Box sx={{ maxWidth: 620 }}>
              <Typography variant="h2" fontSize={24}>
                Something not working?
              </Typography>
              <Typography fontSize={16} color={tokens.text2} mt={0.75}>
                If an app shows Operational but isn’t working for you, tell IT Lab which app, what you tried and when it started.
              </Typography>
            </Box>
            <Button component={RouterLink} to="/request" variant="contained" size="large" endIcon={<ArrowForwardIcon />} sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, flex: 'none' }}>
              Report a problem
            </Button>
          </Stack>
        </Stack>
      </Section>
    </>
  );
}
